import { db, auth } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';

/**
 * logError - writes an error to Firestore logs collection
 * and sends a push notification to admin.
 * 
 * Enhanced with graceful permission handling - will fallback to console
 * if Firestore permissions are insufficient.
 *
 * Usage:
 *   import { logError } from '../utils/errorLogger';
 *   logError('BuySessions', 'Failed to load plans', err.message);
 */
export async function logError(page, error, details = '') {
  // Always log to console first for immediate debugging
  console.error(`[${page}] Error:`, error);
  if (details) {
    console.error(`[${page}] Details:`, details);
  }

  try {
    const user = auth.currentUser;
    
    // Attempt to write to Firestore logs collection
    await addDoc(collection(db, 'logs'), {
      page,
      error: String(error),
      details: String(details).slice(0, 500),
      userId: user?.uid || 'anonymous',
      userEmail: user?.email || 'anonymous',
      timestamp: serverTimestamp(),
    });

    // Send push notification to admin (only if Firestore write succeeded)
    try {
      const functions = getFunctions(app, 'us-central1');
      const notify = httpsCallable(functions, 'sendPushNotification');
      await notify({
        target: 'all',
        title: '⚠️ App Error',
        body: `${page}: ${String(error).slice(0, 100)}`,
        tokens: [],
      });
    } catch (notifyErr) {
      console.warn('Admin notification failed (this is normal for non-critical errors):', notifyErr.message);
    }
  } catch (logErr) {
    // Handle permission errors gracefully
    if (logErr.code === 'permission-denied') {
      console.warn(`[${page}] Error logged to console only (insufficient Firestore permissions):`, {
        error: String(error),
        details: String(details).slice(0, 500),
        userId: auth.currentUser?.uid || 'anonymous',
        userEmail: auth.currentUser?.email || 'anonymous',
        timestamp: new Date().toISOString()
      });
    } else {
      console.error('Failed to write error log to Firestore:', logErr);
      console.error('Original error was:', { page, error, details });
    }
  }
}

/**
 * logInfo - writes an info message to console and optionally to Firestore
 * Used for non-error tracking and debugging
 */
export async function logInfo(page, message, details = '') {
  console.info(`[${page}] Info:`, message);
  if (details) {
    console.info(`[${page}] Details:`, details);
  }

  // Only attempt Firestore write for admins/coaches to avoid permission spam
  try {
    const user = auth.currentUser;
    if (!user) return;

    // Simple role check without additional Firestore queries
    const userDoc = await import('../firebase').then(f => import('firebase/firestore').then(fs => 
      fs.getDoc(fs.doc(f.db, 'users', user.uid))
    ));
    
    if (userDoc.exists()) {
      const roles = userDoc.data().roles || [];
      const isAdminOrCoach = roles.some(r => ['admin', 'coach', 'assistant_coach'].includes(r));
      
      if (isAdminOrCoach) {
        await addDoc(collection(db, 'logs'), {
          page,
          type: 'info',
          message: String(message),
          details: String(details).slice(0, 500),
          userId: user.uid,
          userEmail: user.email,
          timestamp: serverTimestamp(),
        });
      }
    }
  } catch (err) {
    // Silently fail for info logs
    console.debug('Info log to Firestore failed (this is normal):', err.code);
  }
}
